import { useMemo, useRef, useState } from "react";
import { Search, X, Eye, EyeOff, Settings2, Save, Trash2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useChartSettings } from "@/hooks/useChartSettings";
import { useSettingsStore } from "@/stores/settingsStore";
import { type IndicatorCategory } from "@/lib/lytick/chart-config";
import { INDICATOR_CATALOG, getCatalogEntry, type IndicatorCatalogEntry } from "@/lib/lytick/indicatorRegistry";
import { IndicatorSettings } from "@/components/lytick/indicators/IndicatorSettings";
import type { IndicatorInstance, IndicatorTemplate } from "@/types/indicator";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

interface IndicatorPanelProps {
  open: boolean;
  onClose: () => void;
}

const CATEGORY_COLORS: Record<IndicatorCategory, string> = {
  trend: "var(--info)",
  momentum: "var(--warning)",
  volatility: "#A855F7",
};

const ALL_CATEGORIES: { id: IndicatorCategory | "all"; label: string }[] = [
  { id: "all", label: "All" },
  { id: "trend", label: "Trend" },
  { id: "momentum", label: "Momentum" },
  { id: "volatility", label: "Volatility" },
];

export function IndicatorPanel({ open, onClose }: IndicatorPanelProps) {
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<IndicatorCategory | "all">("all");
  const [settingsInstanceId, setSettingsInstanceId] = useState<string | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");

  const onChartRef = useRef<HTMLDivElement>(null);

  const { activeIndicatorCount, indicators, addIndicator, removeIndicator, addIndicatorFromCatalog, removeIndicatorById, updateIndicatorById } =
    useChartSettings();
  const { indicatorTemplates, addIndicatorTemplate, removeIndicatorTemplate, recentIndicators, recordRecentIndicator } = useSettingsStore();

  const recentEntries = useMemo(
    () => recentIndicators.map((name) => getCatalogEntry(name)).filter(Boolean) as IndicatorCatalogEntry[],
    [recentIndicators],
  );

  const handleAddFromCatalog = (entry: IndicatorCatalogEntry) => {
    recordRecentIndicator(entry.name);
    addIndicatorFromCatalog(entry);
    setTimeout(() => onChartRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);
  };

  const loadTemplate = (template: IndicatorTemplate) => {
    indicators.forEach((inst) => removeIndicatorById(inst.id));
    template.indicators.forEach((tpl) => {
      const instance: IndicatorInstance = {
        ...tpl,
        id: crypto.randomUUID(),
        paneId: tpl.isOverlay ? "candle_pane" : "",
      };
      addIndicator(instance);
    });
  };

  const saveAsTemplate = () => {
    const name = newTemplateName.trim() || `Template ${indicatorTemplates.length + 1}`;
    const template: IndicatorTemplate = {
      id: crypto.randomUUID(),
      name,
      indicators: indicators.map((inst) => {
        const { id, paneId, ...rest } = inst;
        return rest;
      }),
      createdAt: Date.now(),
    };
    addIndicatorTemplate(template);
    setNewTemplateName("");
  };

  const deleteTemplate = (id: string) => {
    removeIndicatorTemplate(id);
  };

  const normalizedQuery = query.trim().toLowerCase();

  const activeInstances = useMemo(
    () => indicators.filter((inst) => getCatalogEntry(inst.name) !== undefined),
    [indicators],
  );

  const settingsInstance = useMemo(
    () => indicators.find((i) => i.id === settingsInstanceId) ?? null,
    [indicators, settingsInstanceId],
  );

  const filteredCatalog = useMemo(() => {
    let items = INDICATOR_CATALOG;
    if (categoryFilter !== "all") {
      items = items.filter((e) => e.category === categoryFilter);
    }
    if (normalizedQuery) {
      items = items.filter(
        (e) =>
          e.displayName.toLowerCase().includes(normalizedQuery) ||
          e.shortName.toLowerCase().includes(normalizedQuery) ||
          e.description.toLowerCase().includes(normalizedQuery),
      );
    }
    return items;
  }, [categoryFilter, normalizedQuery]);

  const handlePanelClose = (v: boolean) => {
    if (!v) {
      setSettingsInstanceId(null);
      onClose();
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handlePanelClose}>
        <DialogContent
          showCloseButton={false}
          className="max-w-[640px] p-0 gap-0 flex flex-col overflow-hidden"
          style={{
            backgroundColor: "var(--bg-surface)",
            borderColor: "var(--border-default)",
            maxHeight: "80vh",
          }}
        >
          <DialogTitle className="sr-only">Indicators</DialogTitle>

          {/* Header */}
          <div
            className="flex h-10 shrink-0 items-center justify-between border-b px-3"
            style={{
              backgroundColor: "var(--bg-elevated)",
              borderColor: "var(--border-default)",
            }}
          >
            <div className="flex items-center gap-2">
              <span style={{ color: "var(--text-primary)", fontSize: "var(--text-base)", fontWeight: 700 }}>
                Indicators
              </span>
              {activeIndicatorCount > 0 && (
                <span
                  className="inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5"
                  style={{
                    backgroundColor: "color-mix(in srgb, var(--accent-primary) 15%, transparent)",
                    color: "var(--accent-primary)",
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                >
                  {activeIndicatorCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {activeIndicatorCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowTemplates(true)}
                  className="interactive interactive-icon inline-flex h-7 w-7 items-center justify-center rounded-md"
                  style={{ color: "var(--text-secondary)" }}
                  aria-label="Templates"
                >
                  <Save className="h-3.5 w-3.5" />
                </button>
              )}
              <DialogClose asChild>
                <button
                  type="button"
                  className="interactive interactive-icon inline-flex h-7 w-7 items-center justify-center rounded-md"
                  style={{ color: "var(--text-secondary)" }}
                  aria-label="Close indicators"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </DialogClose>
            </div>
          </div>

          {/* Search */}
          <div className="shrink-0 border-b px-3 py-2" style={{ borderColor: "var(--border-default)" }}>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2"
                style={{ color: "var(--text-muted)" }}
              />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search indicators..."
                className="workspace-input h-7 border pl-8 text-xs shadow-none"
                style={{
                  backgroundColor: "var(--bg-elevated)",
                  borderColor: "var(--border-default)",
                  color: "var(--text-primary)",
                }}
                autoFocus
              />
            </div>
          </div>

          {/* Scrollable content */}
          <div className="min-h-0 flex-1 overflow-y-auto custom-scrollbar">
            {/* On Chart — active instances */}
            {activeInstances.length > 0 && !normalizedQuery && (
              <div ref={onChartRef} className="px-1 pt-2 pb-1">
                <div className="px-2 pb-1">
                  <span style={{ fontSize: 9, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    On Chart · {activeInstances.length}
                  </span>
                </div>
                {activeInstances.map((inst) => (
                  <ActiveInstanceRow
                    key={inst.id}
                    instance={inst}
                    onToggleVisibility={() => updateIndicatorById(inst.id, { visible: !inst.visible })}
                    onOpenSettings={() => setSettingsInstanceId(inst.id)}
                    onRemove={() => removeIndicatorById(inst.id)}
                  />
                ))}
                <div className="mx-3 mt-2" style={{ height: 1, backgroundColor: "var(--border-subtle)" }} />
              </div>
            )}

            {/* Recently used */}
            {recentEntries.length > 0 && !normalizedQuery && (
              <div className="px-1 pt-2 pb-1">
                <div className="px-2 pb-1">
                  <span style={{ fontSize: 9, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    Recently used
                  </span>
                </div>
                {recentEntries.map((entry) => {
                  const activeCount = activeInstances.filter((i) => i.name === entry.name).length;
                  return (
                    <CatalogRow
                      key={`recent-${entry.name}`}
                      entry={entry}
                      activeCount={activeCount}
                      onAdd={() => handleAddFromCatalog(entry)}
                    />
                  );
                })}
                <div className="mx-3 mt-1" style={{ height: 1, backgroundColor: "var(--border-subtle)" }} />
              </div>
            )}

            {/* Category filter pills */}
            <div className="flex flex-wrap gap-1 px-3 pt-2 pb-1">
              {ALL_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setCategoryFilter(cat.id)}
                  className="px-2 py-0.5 rounded-full text-xxs font-semibold transition-colors"
                  style={{
                    backgroundColor: categoryFilter === cat.id
                      ? "color-mix(in srgb, var(--accent-primary) 15%, transparent)"
                      : "transparent",
                    color: categoryFilter === cat.id ? "var(--accent-primary)" : "var(--text-muted)",
                    border: categoryFilter === cat.id ? "1px solid color-mix(in srgb, var(--accent-primary) 25%, transparent)" : "1px solid transparent",
                  }}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Available indicators */}
            <div className="px-1 py-1">
              {filteredCatalog.map((entry) => {
                const activeCount = activeInstances.filter((i) => i.name === entry.name).length;
                return (
                  <CatalogRow
                    key={entry.name}
                    entry={entry}
                    activeCount={activeCount}
                    onAdd={() => handleAddFromCatalog(entry)}
                  />
                );
              })}

              {filteredCatalog.length === 0 && (
                <div className="px-3 py-8 text-center">
                  <p style={{ color: "var(--text-secondary)", fontSize: "var(--text-sm)" }}>
                    No indicators found
                  </p>
                  <p className="mt-1" style={{ color: "var(--text-muted)", fontSize: "var(--text-xs)" }}>
                    Try a different search term.
                  </p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings dialog — sibling, not nested */}
      {settingsInstance && (
        <IndicatorSettings
          instance={settingsInstance}
          open={settingsInstanceId !== null}
          onClose={() => setSettingsInstanceId(null)}
        />
      )}

      {/* Templates dialog — sibling, not nested */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="max-w-[360px] p-0 gap-0" style={{ backgroundColor: "var(--bg-surface)", borderColor: "var(--border-default)" }}>
          <div className="px-4 pt-4 pb-2 border-b" style={{ borderColor: "var(--border-default)" }}>
            <div className="text-sm font-semibold flex items-center justify-between" style={{ color: "var(--text-primary)" }}>
              <DialogTitle className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
                Indicator Templates
              </DialogTitle>
              <button onClick={() => setShowTemplates(false)} className="inline-flex items-center justify-center rounded-md h-6 w-6" style={{ color: "var(--text-secondary)" }}>
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          <div className="px-4 py-3 max-h-[50vh] overflow-y-auto">
            {/* Save current as template */}
            <div className="mb-3 pb-3 border-b" style={{ borderColor: "var(--border-subtle)" }}>
              <p className="text-xs mb-2" style={{ color: "var(--text-secondary)" }}>
                Save current indicators ({activeIndicatorCount}) as template:
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  placeholder="Template name"
                  className="flex-1 h-8 text-xs rounded-md border px-2"
                  style={{
                    backgroundColor: "var(--bg-elevated)",
                    borderColor: "var(--border-default)",
                    color: "var(--text-primary)",
                  }}
                />
                <button
                  onClick={saveAsTemplate}
                  disabled={activeIndicatorCount === 0}
                  className="h-8 px-3 rounded-md text-xs font-medium disabled:opacity-50"
                  style={{
                    backgroundColor: "var(--accent-primary)",
                    color: "white",
                  }}
                >
                  Save
                </button>
              </div>
            </div>

            {/* Template list */}
            {indicatorTemplates.length === 0 ? (
              <p className="text-xs text-center py-4" style={{ color: "var(--text-muted)" }}>
                No saved templates
              </p>
            ) : (
              <div className="space-y-1">
                {indicatorTemplates.map((tpl) => (
                  <div
                    key={tpl.id}
                    className="flex items-center justify-between gap-2 px-2 py-2 rounded-md"
                    style={{ backgroundColor: "var(--bg-elevated)" }}
                  >
                    <button
                      onClick={() => {
                        loadTemplate(tpl);
                        setShowTemplates(false);
                      }}
                      className="text-left flex-1 min-w-0"
                    >
                      <p className="text-xs font-medium truncate" style={{ color: "var(--text-primary)" }}>
                        {tpl.name}
                      </p>
                      <p className="text-xxs" style={{ color: "var(--text-muted)" }}>
                        {tpl.indicators.length} indicator{tpl.indicators.length !== 1 ? "s" : ""}
                      </p>
                    </button>
                    <button
                      onClick={() => deleteTemplate(tpl.id)}
                      className="flex-shrink-0 h-6 w-6 rounded flex items-center justify-center"
                      style={{ color: "var(--text-muted)" }}
                      aria-label="Delete template"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Active Instance Row ────────────────────────────────────────────────

function ActiveInstanceRow({
  instance,
  onToggleVisibility,
  onOpenSettings,
  onRemove,
}: {
  instance: IndicatorInstance;
  onToggleVisibility: () => void;
  onOpenSettings: () => void;
  onRemove: () => void;
}) {
  const catalog = getCatalogEntry(instance.name);
  const label = instance.nickname || (catalog
    ? `${catalog.shortName} (${instance.calcParams.join(", ")})`
    : instance.name);

  return (
    <div
      className="group relative flex items-center justify-between gap-1 rounded-md px-2 transition-colors"
      style={{
        height: 32,
        backgroundColor: "color-mix(in srgb, var(--accent-primary) 8%, transparent)",
      }}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onToggleVisibility}
              className="flex-shrink-0 inline-flex items-center justify-center h-5 w-5 rounded"
              style={{ color: instance.visible ? "var(--accent-primary)" : "var(--text-muted)" }}
              aria-label={instance.visible ? "Hide indicator" : "Show indicator"}
            >
              {instance.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={4}>
            {instance.visible ? "Hide indicator" : "Show indicator"}
          </TooltipContent>
        </Tooltip>
        <span
          className="truncate"
          style={{
            color: instance.visible ? "var(--text-primary)" : "var(--text-muted)",
            fontSize: "var(--text-sm)",
            fontWeight: 500,
          }}
        >
          {label}
        </span>
      </div>
      <div className="flex items-center gap-0.5 flex-shrink-0">
        <button
          onClick={onOpenSettings}
          className="inline-flex items-center justify-center h-5 w-5 rounded opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ color: "var(--text-secondary)" }}
          aria-label="Settings"
        >
          <Settings2 className="h-3 w-3" />
        </button>
        <button
          onClick={onRemove}
          className="inline-flex items-center justify-center h-5 w-5 rounded opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ color: "var(--text-muted)" }}
          aria-label="Remove"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

// ── Catalog Row ────────────────────────────────────────────────────────

function CatalogRow({
  entry,
  activeCount,
  onAdd,
}: {
  entry: IndicatorCatalogEntry;
  activeCount: number;
  onAdd: () => void;
}) {
  const isActive = activeCount > 0;
  const [pulse, setPulse] = useState(false);

  const handleAdd = () => {
    setPulse(true);
    setTimeout(() => setPulse(false), 300);
    onAdd();
  };

  return (
    <button
      type="button"
      onClick={handleAdd}
      className="group flex w-full items-center gap-3 rounded-md px-3 transition-colors hover:bg-[color-mix(in_srgb,var(--bg-elevated)_50%,transparent)]"
      style={{
        height: 32,
        backgroundColor: pulse ? "color-mix(in srgb, var(--accent-primary) 10%, transparent)" : undefined,
        transition: "background-color 300ms ease",
        textAlign: "left",
        cursor: "pointer",
      }}
      title={entry.description}
      aria-label={isActive ? `Add another ${entry.displayName}` : `Add ${entry.displayName}`}
    >
      <span
        className="flex-shrink-0 rounded-full"
        style={{ width: 6, height: 6, backgroundColor: CATEGORY_COLORS[entry.category] }}
      />
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span
          className="truncate"
          style={{ color: "var(--text-secondary)", fontSize: "var(--text-sm)", fontWeight: 500 }}
        >
          {entry.displayName}
        </span>
        {isActive && (
          <span
            className="flex-shrink-0 rounded-full px-1.5 text-xxs font-semibold"
            style={{
              backgroundColor: "color-mix(in srgb, var(--accent-primary) 15%, transparent)",
              color: "var(--accent-primary)",
            }}
          >
            {activeCount}
          </span>
        )}
      </div>
    </button>
  );
}
